import { Pie, PieChart, Cell } from "recharts";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "@/components/ui/chart";
import type { WasteStatsBucket } from "@/hooks/use-waste-stats";
import { useTranslation } from "@/i18n/use-translation";
import { formatWeight } from "@/lib/format";

import { ChartCard } from "./chart-card";

const FALLBACK = "#9ca3af";

type Props = {
  title: string;
  description: string;
  buckets: WasteStatsBucket[];
  labelOf: (key: string) => string;
  colorOf: (key: string) => string;
};

/** Donut breakdown of waste weight by a categorical dimension (waste type, service time). */
export function BreakdownDonut({ title, description, buckets, labelOf, colorOf }: Props) {
  const { locale } = useTranslation();
  const data = buckets.filter((b) => b.total_weight > 0);
  const config: ChartConfig = Object.fromEntries(
    buckets.map((b) => [b.key, { label: labelOf(b.key), color: colorOf(b.key) ?? FALLBACK }]),
  );

  return (
    <ChartCard title={title} description={description} isEmpty={data.length === 0}>
      {/* Fixed height (not aspect-square) so a 3-line legend doesn't squeeze the
          plot area and shift the pie up until its top clips. outerRadius stays
          90 below, so the donut itself is the same size. */}
      <ChartContainer config={config} className="mx-auto h-[300px] w-full">
        <PieChart>
          <ChartTooltip
            content={
              <ChartTooltipContent
                nameKey="key"
                formatter={(value, name) =>
                  `${config[name as string]?.label ?? name}: ${formatWeight(locale, Number(value))}`
                }
              />
            }
          />
          {/* Fixed radius so the donut is the same size regardless of how many
              lines the legend wraps to (auto-sizing shrinks the pie otherwise). */}
          <Pie
            data={data}
            dataKey="total_weight"
            nameKey="key"
            innerRadius={60}
            outerRadius={90}
            strokeWidth={2}
          >
            {data.map((b) => (
              <Cell key={b.key} fill={colorOf(b.key) ?? FALLBACK} />
            ))}
          </Pie>
          <ChartLegend content={<ChartLegendContent nameKey="key" />} className="flex-wrap" />
        </PieChart>
      </ChartContainer>
    </ChartCard>
  );
}
